import { Link, useSearchParams } from "react-router-dom";
import { DollarSign, Crown, Calendar, CreditCard, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState, useEffect, useRef } from "react";
import { OnboardingChecklist } from "@/components/dashboard/OnboardingChecklist";
import { PRICING } from "@/config/pricing";
import { CreditUsageWidget } from "@/components/dashboard/CreditUsageWidget";
import { CreatorJourney } from "@/components/dashboard/CreatorJourney";
import { QuickActions } from "@/components/dashboard/QuickActions";
import { RecentActivity } from "@/components/dashboard/RecentActivity";
import { DashboardStats } from "@/components/dashboard/DashboardStats";
import { WhatsNew } from "@/components/dashboard/WhatsNew";

export default function Dashboard() {
  const { user, subscription } = useAuth();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const welcomeEmailSent = useRef(false);

  // Send welcome email and create onboarding record after successful checkout
  useEffect(() => {
    const handleSuccessfulCheckout = async () => {
      if (searchParams.get("success") !== "true" || welcomeEmailSent.current || !user?.email) return;
      welcomeEmailSent.current = true;
      try {
        const plan = subscription.plan === "yearly" ? "yearly" : "monthly";
        const name = user.user_metadata?.full_name || user.email?.split("@")[0] || "Creator";
        await supabase.functions.invoke("send-welcome-email", { body: { email: user.email, name, plan } });
        await supabase.from("onboarding_progress").upsert({ user_id: user.id, email: user.email, name, subscribed_at: new Date().toISOString() }, { onConflict: "user_id" });
        await supabase.from("checkout_attempts").update({ completed_at: new Date().toISOString() }).eq("user_id", user.id).is("completed_at", null);
        toast.success("Welcome! Check your email for getting started tips.");
        searchParams.delete("success");
        setSearchParams(searchParams, { replace: true });
      } catch (error) {
        console.error("Failed to send welcome email:", error);
      }
    };
    handleSuccessfulCheckout();
  }, [searchParams, user, subscription.plan, setSearchParams]);

  const handleManageSubscription = async () => {
    setIsLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.error === "no_customer") { toast.info(data.message || "No billing history found"); return; }
      if (data?.url) window.open(data.url, "_blank");
    } catch (error: any) {
      toast.error(error.message || "Failed to open billing portal");
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  };

  return (
    <div className="p-6 md:p-8 space-y-8">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold mb-2">Welcome back! 👋</h1>
          <p className="text-muted-foreground">Here's what's happening in your studio today.</p>
        </div>
        <Link
          to="/app/monetization-ai"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
        >
          <DollarSign className="w-5 h-5" />
          Monetisation AI
        </Link>
      </div>

      {/* Quick Actions */}
      <QuickActions />

      {/* Onboarding Checklist */}
      <OnboardingChecklist />

      {/* Free Trial Upgrade Banner */}
      {!subscription.subscribed && !subscription.isLifetime && (
        <div className="glass-card p-5 border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <h3 className="font-display text-lg font-semibold mb-1">You're on the Free Trial</h3>
              <p className="text-sm text-muted-foreground">
                You're on the Free Trial — you have access to Nexis AI and Keyword Research only. Upgrade to unlock Monetisation AI, Clip AI, Email List Builder, Sales Page Builder, and Launch Advisor — plus up to 2,000 credits/month.
              </p>
              <p className="text-xs text-muted-foreground mt-1">No commitment. Cancel anytime.</p>
            </div>
            <Link
              to="/checkout"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              <Sparkles className="w-4 h-4" />
              Upgrade — From $39/mo
            </Link>
          </div>
        </div>
      )}

      {/* Stats & Tips */}
      <DashboardStats />

      {/* Creator Journey */}
      <CreatorJourney />

      {/* Credits Card */}
      <CreditUsageWidget />

      {/* Recent Activity + What's New */}
      <div className="grid lg:grid-cols-2 gap-6">
        <RecentActivity />
        <WhatsNew />
      </div>

      {/* Subscription Management Section */}
      <div className="glass-card p-6 border-primary/20">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-neon-purple to-neon-yellow flex items-center justify-center">
            <Crown className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold">Subscription</h2>
            <p className="text-sm text-muted-foreground">Manage your AI StoryCraft Studio membership</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
              <div>
                <p className="text-sm text-muted-foreground">Plan</p>
                <p className="font-semibold text-foreground">
                  {subscription.isLifetime
                    ? "Lifetime Access"
                    : subscription.subscribed && subscription.planTier
                      ? `${PRICING[subscription.planTier as keyof typeof PRICING]?.name || subscription.planTier} ($${PRICING[subscription.planTier as keyof typeof PRICING]?.price ?? 0}/month)`
                      : subscription.plan === "yearly"
                        ? "Annual (Legacy)"
                        : subscription.subscribed
                          ? "Monthly (Legacy)"
                          : `Free Trial ($0/month)`}
                </p>
              </div>
              {subscription.isLifetime ? (
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Lifetime Member
                </span>
              ) : (
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  subscription.status === "active"
                    ? "bg-green-500/20 text-green-400"
                    : subscription.status === "past_due"
                      ? "bg-yellow-500/20 text-yellow-400"
                      : "bg-destructive/20 text-destructive"
                }`}>
                  {subscription.status === "active" ? "Active" :
                   subscription.status === "past_due" ? "Past Due" :
                   subscription.status || "Inactive"}
                </span>
              )}
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
              <div>
                <p className="text-sm text-muted-foreground">
                  {subscription.isLifetime ? "Access" : subscription.status === "canceled" ? "Access Until" : "Next Billing"}
                </p>
                <p className="font-semibold text-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  {subscription.isLifetime
                    ? "Forever"
                    : subscription.subscriptionEnd
                      ? formatDate(subscription.subscriptionEnd)
                      : "—"}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {!subscription.isLifetime && (
              <button
                onClick={handleManageSubscription}
                disabled={isLoadingPortal}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <CreditCard className="w-4 h-4" />
                {isLoadingPortal ? "Opening Portal..." : "Manage Billing"}
              </button>
            )}
            <p className="text-xs text-muted-foreground text-center">
              {subscription.isLifetime
                ? "You have lifetime access to all AI StoryCraft Studio features."
                : "Update payment method, change plan, or cancel anytime."}
            </p>
            {subscription.stripeCustomerId && (
              <div className="pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Customer ID: <span className="font-mono">{subscription.stripeCustomerId.slice(0, 12)}...</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
